import type { CSSProperties, ReactNode } from 'react';

import { useQuery } from '@apollo/client/react';

import { GET_APP_SETTINGS } from '#src/graphql/queries/app-settings';
import { AppSettingsProvider } from '#src/providers/app-settings-provider';

interface AppCacheBootstrapProps {
  children: ReactNode;
}

const shellStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 'var(--app-viewport-height)',
  padding: '1.5rem',
  fontFamily: "'Lexend', system-ui, sans-serif",
  textAlign: 'center',
};

const headingStyle: CSSProperties = {
  fontSize: '1.25rem',
  marginBottom: '0.5rem',
};

const messageStyle: CSSProperties = {
  color: '#666',
  fontSize: '0.875rem',
  maxWidth: '28rem',
};

const buttonStyle: CSSProperties = {
  padding: '0.5rem 0.75rem',
  fontSize: '0.875rem',
  fontFamily: 'inherit',
  border: '1px solid #3182ce',
  borderRadius: '4px',
  background: '#3182ce',
  color: '#fff',
  cursor: 'pointer',
  marginTop: '1rem',
};

function AppCacheLoading() {
  return (
    <div style={shellStyle}>
      <p style={{ margin: 0 }}>Loading…</p>
    </div>
  );
}

function AppCacheBootstrapError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div style={shellStyle}>
      <h1 style={headingStyle}>Could not load the app</h1>
      <p style={messageStyle}>{message}</p>
      <button type="button" style={buttonStyle} onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

/**
 * Loads app settings before rendering the rest of the tree.
 * The reaction catalog is fetched after login (see {@link AuthGuard}).
 * On failure, shows a retriable error (no placeholder defaults).
 */
export function AppCacheBootstrap({ children }: AppCacheBootstrapProps) {
  const appQ = useQuery(GET_APP_SETTINGS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const appSettings = appQ.data?.appSettings;
  const ready = appSettings != null;

  const hasError = appQ.error != null;
  const inFlight = appQ.loading;

  const retry = () => {
    void appQ.refetch();
  };

  if (ready) {
    return (
      <AppSettingsProvider value={appSettings}>{children}</AppSettingsProvider>
    );
  }

  if (!hasError && inFlight) {
    return <AppCacheLoading />;
  }

  const message =
    appQ.error?.message || 'Something went wrong while loading settings.';

  return <AppCacheBootstrapError message={message} onRetry={retry} />;
}
